/**
 * Envelope codec — PROTOCOL.md §2. Validates the four-field envelope and the
 * 8 KiB frame cap; unknown `type` values decode fine (forward compatibility)
 * and are ignored by the dispatchers.
 */
import { MAX_MESSAGE_BYTES } from './constants.js';
import type { WSMessage } from './messages.js';

const encoder = new TextEncoder();

/** Serialize one message (adds the sender timestamp). */
export function encodeMessage<T>(type: string, payload: T, seq: number): string {
  const msg: WSMessage<T> = { type, seq, payload, timestamp: Date.now() };
  return JSON.stringify(msg);
}

export type DecodeError = 'TOO_LARGE' | 'BAD_JSON' | 'BAD_SHAPE';

export type DecodeResult = { ok: true; message: WSMessage } | { ok: false; error: DecodeError };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Parse and structurally validate one raw text frame. */
export function decodeMessage(raw: string): DecodeResult {
  if (encoder.encode(raw).length > MAX_MESSAGE_BYTES) return { ok: false, error: 'TOO_LARGE' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'BAD_JSON' };
  }
  if (!isRecord(parsed)) return { ok: false, error: 'BAD_SHAPE' };
  const { type, seq, payload, timestamp } = parsed;
  if (typeof type !== 'string' || type.length === 0) return { ok: false, error: 'BAD_SHAPE' };
  if (typeof seq !== 'number' || !Number.isInteger(seq) || seq < 0) {
    return { ok: false, error: 'BAD_SHAPE' };
  }
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
    return { ok: false, error: 'BAD_SHAPE' };
  }
  if (!('payload' in parsed)) return { ok: false, error: 'BAD_SHAPE' };
  return { ok: true, message: { type, seq, payload, timestamp } };
}

/** Type-safe view of a decoded message once its `type` is known. */
export function asPayload<T>(message: WSMessage): T {
  return message.payload as T;
}
